import React, { useEffect } from "react";
import { DocumentListPage } from "../../pages/document/DocumentListPage";
import Home from "../Home";
import { NewObjectPage } from "../../pages/coobjects/NewObjectPage";
import { EditObjectPage } from "../../pages/coobjects/EditObjectPage";
import NotFoundPage from "../../pages/infos/NotFoundPage";
import { NewDocumentPage } from "../../pages/document/NewDocumentPage";
import { DocumentPropertiesPage } from "../../pages/document/DocumentPropertiesPage";
import { SettingsPage } from "../../pages/settings/SettingsPage";
import {
  CHOOSECONFIG,
  COPY_CUT_PASTE_PANEL_MESSAGE,
  DETAILS_DOCUMENT,
  DOCUMENT_PROPERTIES,
  DOCUMENTS_LISTS,
  DOWNLOAD_COPY,
  EDIT_OBJECT,
  HOME,
  IMPORT,
  INFO,
  IXBRL,
  MAINTENANCEMODE,
  MOVE,
  NEW_DOCUMENT,
  NEW_OBJECT,
  OBJECTS_LIST,
  REFRESH_MULTI,
  SETTINGS,
  SOURCES,
  SYNCH_RESTORE,
  TEMPLATE,
  TROUBLESHOOTING_TOOLS,
  UPDATES,
  VALIDATION,
  WELCOME,
} from "../../utils/constants/navigation";
import { useAppSelector } from "../../redux/hooks/hooks";
import { historyNavigation } from "../../redux/feature/general/navigation-slice";
import { SynchRestorePage } from "../../pages/document/SynchRestorePage";
import { ChooseConfigPage } from "../../pages/login/ChooseConfigPage";
import {
  configurationsNotEmpty,
  externalConfigAvailable,
  selectActiveConfiguration,
  selectIsConfigured,
} from "../../redux/feature/settings/configurations-slice";
import { ImportDocumentPage } from "../../pages/document/ImportDocumentPage";
import { ObjectsListPage } from "../../pages/coobjects/ObjectsListPage";
import { UpdatesPage } from "../../pages/document/UpdatesPage";
import { DocumentSourcesPage } from "../../pages/document/DocumentSourcesPage";
import { TagetikInfo } from "../../pages/infos/TagetikInfo";
import { MoveObjectPage } from "../../pages/coobjects/MoveObjectPage";
import { SeahorsePage } from "../../pages/seahorse/SeahorsePage";
import { CopyCutPasteMessagePanel } from "../layout/CopyCutPasteMessagePanel";
import { validateString } from "../../utils/helpers/ObjectCheckerHelper";
import { DownloadCopyPage } from "../../pages/document/DownloadCopyPage/DownloadCopyPage";
import { MaintenanceMode } from "../generic/MaintenanceMode";
import { WelcomePage } from "../../pages/settings/WelcomePage";
import { useDispatch } from "react-redux";
import { fetchExternalConfigs } from "../../redux/feature/settings/external-configs-slice";
import { AppDispatch } from "../../redux/store/store";
import LoadingManager from "../../common/loading/LoadingManager";
import { FETCH_EXTERNAL_CONFIGS } from "../../utils/constants/notification";
import { TroubleshootingToolsPage } from "../../pages/infos/troubleshooting/TroubleshootingToolsPage";
import { ValidationPage } from "../../pages/document/ValidationPage";

export const NavigationHandler: React.FunctionComponent = () => {
  const pageHistoryNavigation = useAppSelector(historyNavigation);
  const isConfigured = useAppSelector(selectIsConfigured);
  const presenceOfConfigurations = useAppSelector(configurationsNotEmpty);
  const presenceOfExternalConfig = useAppSelector(externalConfigAvailable);
  const activeConfiguration = useAppSelector(selectActiveConfiguration);

  const dispatch = useDispatch<AppDispatch>();

  const [page, setPage] = React.useState<{
    pageName: string;
    pageElement: React.ReactElement;
  }>({ pageName: DOCUMENT_PROPERTIES, pageElement: null });

  useEffect(() => {
    if (pageHistoryNavigation && pageHistoryNavigation.length > 0) {
      const page = pageHistoryNavigation[pageHistoryNavigation.length - 1];
      setPage({ pageName: page, pageElement: null });
    }
  }, [pageHistoryNavigation]);

  useEffect(() => {
    if (isConfigured && activeConfiguration?.authData) {
      if (
        validateString(activeConfiguration.authData.access_token) &&
        Date.now() < activeConfiguration.authData.expires_at * 1000
      ) {
        dispatch(fetchExternalConfigs());
      }
    } else if (activeConfiguration && !activeConfiguration.authData) {
      setPage((oldPage) => ({
        pageName: oldPage.pageName,
        pageElement: getRender(oldPage.pageName),
      }));
      LoadingManager.remove(FETCH_EXTERNAL_CONFIGS);
    }
  }, [dispatch, isConfigured, activeConfiguration]);

  useEffect(() => {
    if (!presenceOfExternalConfig && needsExternalConfig(page.pageName)) {
      LoadingManager.create(FETCH_EXTERNAL_CONFIGS);
    } else {
      setPage((oldPage) => ({
        pageName: oldPage.pageName,
        pageElement: getRender(oldPage.pageName),
      }));
      LoadingManager.remove(FETCH_EXTERNAL_CONFIGS);
    }
  }, [presenceOfExternalConfig, setPage, page.pageName]);

  useEffect(() => {
    if (!page.pageElement)
      setPage((oldPage) => ({
        pageName: oldPage.pageName,
        pageElement: getRender(oldPage.pageName),
      }));
  }, [page.pageElement]);

  useEffect(() => {
    if (page.pageElement) {
      LoadingManager.remove(FETCH_EXTERNAL_CONFIGS);
    }
  }, [page.pageElement]);

  const needsExternalConfig = (pageName: string) => {
    // TODO: list probably not correct; some pages listed here may not need external config
    return [
      IXBRL,
      EDIT_OBJECT,
      NEW_DOCUMENT,
      DETAILS_DOCUMENT,
      DOCUMENT_PROPERTIES,
      DOCUMENTS_LISTS,
      IMPORT,
      OBJECTS_LIST,
      UPDATES,
      SOURCES,
      DOWNLOAD_COPY,
    ].includes(pageName);
  };

  const getRender = (pageName: string) => {
    if (pageName == CHOOSECONFIG && presenceOfConfigurations) pageName = CHOOSECONFIG;
    else if (pageName == SETTINGS) pageName = SETTINGS;
    else if (!presenceOfConfigurations) pageName = WELCOME;
    else if (!isConfigured || !activeConfiguration.authData) pageName = CHOOSECONFIG;
    else if (!presenceOfExternalConfig && needsExternalConfig(pageName)) return null;

    switch (pageName) {
      case COPY_CUT_PASTE_PANEL_MESSAGE:
        return <CopyCutPasteMessagePanel />;

      case WELCOME:
        return <WelcomePage />;

      case HOME:
        return <Home />;

      case NEW_OBJECT:
        return <NewObjectPage />;

      case EDIT_OBJECT:
        return <EditObjectPage />;

      case IXBRL:
        return <SeahorsePage />;

      case MOVE:
        return <MoveObjectPage />;

      case REFRESH_MULTI:
        return <NotFoundPage />;

      case NEW_DOCUMENT:
        return <NewDocumentPage />;

      case DETAILS_DOCUMENT:
        return <NotFoundPage />;

      case TEMPLATE:
        return <NotFoundPage />;

      case DOCUMENT_PROPERTIES:
        return <DocumentPropertiesPage />;

      case SETTINGS:
        return <SettingsPage />;

      case CHOOSECONFIG:
        return <ChooseConfigPage />;

      case DOCUMENTS_LISTS:
        return <DocumentListPage />;

      case SYNCH_RESTORE:
        return <SynchRestorePage />;

      case IMPORT:
        return <ImportDocumentPage />;

      case VALIDATION:
        return <ValidationPage />;

      case OBJECTS_LIST:
        return <ObjectsListPage />;

      case UPDATES:
        return <UpdatesPage />;

      case SOURCES:
        return <DocumentSourcesPage />;

      case INFO:
        return <TagetikInfo />;

      case DOWNLOAD_COPY:
        return <DownloadCopyPage />;

      case MAINTENANCEMODE:
        return <MaintenanceMode />;

      case TROUBLESHOOTING_TOOLS:
        return <TroubleshootingToolsPage />;

      default:
        console.error("Navigation error!", { pageName });
        return <>NAVIGATION ERROR</>;
    }
  };
  return page.pageElement;
};

export default NavigationHandler;
